import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowsClockwise, UploadSimple, Warning, CheckCircle, XCircle } from "@phosphor-icons/react";

function pick(row, keys) {
  for (const k of Object.keys(row)) {
    if (keys.includes(k.trim().toLowerCase())) return row[k];
  }
  return undefined;
}

function normalizeRollRows(json) {
  return json
    .map((r) => ({
      sku: String(pick(r, ["sku", "variant sku"]) ?? "").trim(),
      batch: String(pick(r, ["batch", "batch number", "batch_number"]) ?? "").trim(),
      brand: String(pick(r, ["brand", "manufacturer", "vendor"]) ?? "").trim(),
      series: String(pick(r, ["series"]) ?? "").trim(),
      product: String(pick(r, ["product", "name", "colour", "color"]) ?? "").trim(),
      current_length_m: Number(pick(r, ["current length (m)", "current length", "current_length_m", "length"]) ?? 0),
      initial_length_m: Number(pick(r, ["initial length (m)", "initial length", "initial_length_m"]) ?? 0) || null,
      width_m: Number(pick(r, ["width (m)", "width", "width_m"]) ?? 0) || 1.52,
      location: String(pick(r, ["location"]) ?? "").trim(),
      manufacturing_date: String(pick(r, ["manufacturing date", "mfg date", "manufacturing_date"]) ?? "").trim(),
      expiry_date: String(pick(r, ["expiry date", "expiry", "expiry_date"]) ?? "").trim(),
      status: String(pick(r, ["status"]) ?? "active").trim().toLowerCase(),
    }))
    .filter((r) => r.brand || r.series || r.product);
}

export default function InventoryReplaceDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("full_by_product");
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setMode("full_by_product"); setRows([]); setFileName(""); setConfirmed(false); setResult(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const norm = normalizeRollRows(json);
      setRows(norm);
      setConfirmed(false);
      if (norm.length === 0) toast.error("No data rows found — check the headers.");
    } catch (err) {
      toast.error("Could not read that file. Use the exported .csv or .xlsx.");
    }
  };

  const distinctProducts = useMemo(() => new Set(rows.map((r) => `${r.brand}|${r.series}|${r.product}`.toLowerCase())).size, [rows]);
  const distinctBatches = useMemo(() => new Set(rows.map((r) => r.batch).filter(Boolean)).size, [rows]);

  const onImport = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/rolls/import-replace", { mode, rows });
      setResult(data);
      toast.success(`Replace complete — removed ${data.deleted_count}, created ${data.created_count}`);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-md" data-testid="import-replace-btn">
          <ArrowsClockwise size={16} /> Import (replace)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowsClockwise size={20} /> Import &amp; replace rolls</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Replace mode</Label>
              <Select value={mode} onValueChange={(v) => { setMode(v); setConfirmed(false); }}>
                <SelectTrigger data-testid="replace-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_by_product">Full replace — per product</SelectItem>
                  <SelectItem value="by_batch">Replace by batch number</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">
                {mode === "full_by_product"
                  ? "For every product in the file, existing (non-incoming) rolls are deleted and rebuilt from the file. Products not in the file are untouched."
                  : "For every batch number in the file, existing (non-incoming) rolls with that batch are deleted and rebuilt from the file. Other batches are untouched."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono tracking-[0.15em] uppercase text-slate-500">Upload exported sheet:</span>
              <label className="inline-flex items-center gap-2 text-xs font-medium h-9 px-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                <UploadSimple size={14} /> Choose .csv / .xlsx
                <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="replace-file-input" />
              </label>
              {fileName && <span className="text-xs text-slate-500 truncate max-w-[220px]">{fileName}</span>}
            </div>

            {rows.length > 0 && (
              <>
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2" data-testid="replace-warning">
                  <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                  <div>
                    <strong>{rows.length}</strong> roll rows loaded.
                    {mode === "full_by_product"
                      ? <> This will <strong>delete &amp; rebuild</strong> all non-incoming rolls for the <strong>{distinctProducts}</strong> product(s) in this file.</>
                      : <> This will <strong>delete &amp; rebuild</strong> all non-incoming rolls for the <strong>{distinctBatches}</strong> batch(es) in this file.</>}
                    {" "}This cannot be undone.
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={confirmed} onCheckedChange={setConfirmed} data-testid="replace-confirm-check" />
                  I understand existing rolls will be deleted and recreated.
                </label>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="replace-import-result">
            <div className="grid grid-cols-3 gap-0 border border-slate-200 rounded-md overflow-hidden">
              <div className="p-4 border-r border-slate-200">
                <XCircle weight="fill" size={18} className="text-red-500" />
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Removed</div>
                <div className="font-mono text-3xl font-semibold mt-1 tabular-nums">{result.deleted_count}</div>
              </div>
              <div className="p-4 border-r border-slate-200">
                <CheckCircle weight="fill" size={18} className="text-emerald-600" />
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Created</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-emerald-700 tabular-nums">{result.created_count}</div>
              </div>
              <div className="p-4">
                <Warning weight="fill" size={18} className="text-amber-500" />
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Errors</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-amber-600 tabular-nums">{result.error_count}</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="border border-amber-200 rounded-md max-h-48 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-3 py-2 border-b last:border-0 border-amber-100 text-xs">
                    <span className="font-mono text-slate-500">Row {e.row}:</span> <span className="text-amber-700">{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={onImport} disabled={submitting || rows.length === 0 || !confirmed} className="bg-red-600 hover:bg-red-700 rounded-md gap-2" data-testid="replace-import-confirm">
                <ArrowsClockwise size={16} /> {submitting ? "Replacing…" : "Replace rolls"}
              </Button>
            </>
          ) : (
            <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="replace-import-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
